import { isNaN, isNil } from 'lodash-es';
import {
  Children,
  isValidElement,
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { WithChildren } from '../../core/types/with-children.js';
import type {
  MarkdownSelection,
  MarkdownSelectionPoint,
} from './markdown-types.js';

/** Returns true if element has children as properties*/
function hasChildren<T>(
  element?: unknown,
): element is { props: WithChildren } & T {
  if (isValidElement<PropsWithChildren>(element)) {
    return !!element?.props?.children;
  }
  return false;
}

/** Get children of element */
export function getChildren<T>(element: T) {
  return Children.toArray(hasChildren(element) ? element.props.children : []);
}

/**
 * Reads the text alignment that a markdown table cell carries as an inline style.
 */
export function getTextAlignment(
  cell: ReactNode,
): 'left' | 'center' | 'right' | undefined {
  if (!isValidElement<{ style?: CSSProperties }>(cell)) {
    return undefined;
  }

  const { textAlign } = cell.props.style ?? {};
  if (textAlign === 'left' || textAlign === 'center' || textAlign === 'right') {
    return textAlign;
  }
  return undefined;
}

/** RegEx to match dash separated language info */
export function matchLanguageParm(className?: string) {
  return /language-(\w+)/.exec(className || '');
}

/**
 * Extracts a MarkdownSelection from two markdown components
 * @param start - Start Element
 * @param end - End Element
 */
export function extractSourcePos(
  start: Element,
  end: Element,
): MarkdownSelection | null {
  const regexp = /^(\d+):(\d+)-(\d+):(\d+)/;
  const match1: RegExpExecArray | null = regexp.exec(
    start.getAttribute('data-sourcepos') ?? '',
  );
  const match2: RegExpExecArray | null = regexp.exec(
    end.getAttribute('data-sourcepos') ?? '',
  );
  if (match1 && match2) {
    const [, start1, sOffset1, end1, eOffset1] = match1
      .filter((m) => !isNaN(m))
      .map((m) => Number(m));
    const [, start2, sOffset2, end2, eOffset2] = match2
      .filter((m) => !isNaN(m))
      .map((m) => Number(m));

    return {
      start: {
        line: start1 < start2 ? start1 : start2,
        column: start1 < start2 ? sOffset1 : sOffset2,
        offset: 0,
      },
      end: {
        line: end1 > end2 ? end1 : end2,
        column: end1 > end2 ? eOffset1 : eOffset2,
        offset: 0,
      },
    };
  }
  return null;
}

/**
 * Recursively traverses the parents to find a valid element with data-sourcepos.
 * Stops after a maximum of 10 recursions
 * @param element - The element whose parent we're looking for
 * @param count - how deep it already is into its recursion
 */
export function findValidParent(
  element: Node | null,
  count: number = 0,
): Element | null {
  if (count > 10 || element === null) {
    return null;
  }
  if (element instanceof Element && element.getAttribute('data-sourcepos')) {
    return element;
  }

  return findValidParent(element.parentNode, count + 1);
}

/**
 * Calculates the offset in the sourceString, given line numbers
 * @param position - a MarkdownSelectionPoint in the document/sourceString
 * @param source - the document/sourceString
 * @param lineOffset - whether we stop on the last line, before, after...
 */
export function findOffset(
  position: MarkdownSelectionPoint,
  source: string,
  lineOffset: number = 0,
): number {
  let offset = 0;
  const lines = source.split('\n');
  for (let i = 0; i < position.line + lineOffset; i++) {
    if (!isNil(lines[i])) {
      offset += lines[i].length + 1;
    }
  }
  return offset;
}
